import { useEffect, useState, useCallback, useMemo } from "react"
import { useSearchParams } from "react-router-dom"

import ProductList from "./ProductList"
import FilterPanel, { ESSENTIALS, typeLabel } from "./ProductFilter"

const PAGE_SIZE = 20

const SORT_OPTIONS = [
  { value: "popular", label: "Most popular" },
  { value: "price_asc", label: "Price: low to high" },
  { value: "price_desc", label: "Price: high to low" },
  { value: "newest", label: "Newest" },
  { value: "name", label: "Name: A–Z" },
]

const peso = (n) => `₱${Number(n).toLocaleString("en-PH")}`

/* =========================
   URL <-> FILTER STATE
   Everything lives in the URL so back/forward and shared links work.
========================= */
const readParams = (params) => ({
  category: params.get("category") || "",
  subs: params.getAll("sub"),
  // "q" is the short form; the nav search bar uses "search"
  search: params.get("search") || params.get("q") || "",
  min: params.get("min") || "",
  max: params.get("max") || "",
  moq: params.get("moq") || "",
  sort: params.get("sort") || "popular",
  page: Math.max(Number(params.get("page")) || 1, 1),
})

const writeParams = (state) => {
  const params = new URLSearchParams()
  if (state.category) params.set("category", state.category)
  state.subs.forEach((s) => params.append("sub", s))
  if (state.search) params.set("search", state.search)
  if (state.min) params.set("min", state.min)
  if (state.max) params.set("max", state.max)
  if (state.moq) params.set("moq", state.moq)
  if (state.sort !== "popular") params.set("sort", state.sort)
  if (state.page > 1) params.set("page", state.page)
  return params
}

export default function ProductPage() {
  const [params, setParams] = useSearchParams()
  const state = useMemo(() => readParams(params), [params])

  const [products, setProducts] = useState([])
  const [facets, setFacets] = useState(null)
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showFilters, setShowFilters] = useState(false)
  const [categories, setCategories] = useState([])
  const [retry, setRetry] = useState(0)

  /* Any filter change goes back to page 1 */
  const update = useCallback(
    (patch) => setParams(writeParams({ ...state, page: 1, ...patch })),
    [state, setParams],
  )

  const reset = () => setParams(writeParams(readParams(new URLSearchParams())))

  /* Categories for the filter panel */
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/categories`)
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => setCategories(Array.isArray(list) ? list : []))
      .catch(() => setCategories([]))
  }, [])

  /* =========================
     FETCH
  ========================= */
  useEffect(() => {
    const controller = new AbortController()

    const load = async () => {
      try {
        setLoading(true)
        setError(null)

        const filters = {}
        if (state.category) filters.category = state.category
        if (state.subs.length)
          filters.subCategory = state.subs.map((s) => (s === ESSENTIALS ? "" : s))
        if (state.min || state.max) filters.price = { min: state.min, max: state.max }
        if (state.moq) filters.min_qty = { max: state.moq }

        const res = await fetch(`${process.env.REACT_APP_API_URL}/products/filter`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            filters,
            search: state.search,
            sort: state.sort,
            page: state.page,
            limit: PAGE_SIZE,
            facets: true,
          }),
          signal: controller.signal,
        })

        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = await res.json()

        setProducts(data.products || [])
        setFacets(data.facets || null)
        setTotalItems(data.totalItems || 0)
        setTotalPages(data.totalPages || 1)
      } catch (err) {
        if (err.name === "AbortError") return
        console.error("FETCH ERROR:", err)
        setError("Failed to load products")
        setProducts([])
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }

    load()
    return () => controller.abort()
  }, [state, retry])

  /* =========================
     ACTIVE FILTER CHIPS
  ========================= */
  // "Kitchen Essentials" only means something where such products exist (Restaurant)
  const essentialsCount = facets?.types?.find((t) => t.value === "")?.count || 0
  const shownSubs = state.subs.filter((s) => s !== ESSENTIALS || essentialsCount > 0)

  const chips = [
    state.category && {
      label: state.category,
      clear: () => update({ category: "", subs: [] }),
    },
    ...shownSubs.map((s) => ({
      label: typeLabel(s),
      clear: () => update({ subs: state.subs.filter((x) => x !== s) }),
    })),
    state.search && { label: `“${state.search}”`, clear: () => update({ search: "" }) },
    (state.min || state.max) && {
      label: state.min && state.max
        ? `${peso(state.min)} – ${peso(state.max)}`
        : state.min ? `${peso(state.min)}+` : `Up to ${peso(state.max)}`,
      clear: () => update({ min: "", max: "" }),
    },
    state.moq && { label: `MOQ ${state.moq} or less`, clear: () => update({ moq: "" }) },
  ].filter(Boolean)

  // Name the cuisine when exactly one is picked (essentials don't count)
  const cuisines = state.subs.filter((s) => s !== ESSENTIALS)
  const picked = cuisines.length === 1 ? cuisines[0] : state.subs.length === 1 ? state.subs[0] : null
  const heading = picked
    ? `${state.category || "Products"} › ${typeLabel(picked)}`
    : state.category || "Products"

  const from = totalItems ? (state.page - 1) * PAGE_SIZE + 1 : 0
  const to = Math.min(state.page * PAGE_SIZE, totalItems)

  /* =========================
     UI
  ========================= */
  const goToPage = (page) => setParams(writeParams({ ...state, page }))

  return (
    <div className="container-fluid my-4">
      <h1 className="mb-3">{heading}</h1>

      {/* TOOLBAR: filter toggle, result count, sort */}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <button
          type="button"
          className={`btn ${showFilters ? "btn-success" : "btn-outline-success"}`}
          aria-expanded={showFilters}
          aria-controls="filter-panel"
          onClick={() => setShowFilters((s) => !s)}
        >
          Filters{chips.length > 0 && ` (${chips.length})`}{" "}
          <span aria-hidden="true">{showFilters ? "▲" : "▼"}</span>
        </button>

        <span className="text-muted me-auto ms-2" aria-live="polite">
          {loading
            ? "Loading…"
            : totalItems
              ? `Showing ${from}–${to} of ${totalItems} products`
              : "No products found"}
        </span>

        <label htmlFor="sort" className="text-muted small">
          Sort by
        </label>
        <select
          id="sort"
          className="form-select form-select-sm w-auto"
          value={state.sort}
          onChange={(e) => update({ sort: e.target.value })}
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {/* DROP-DOWN FILTER PANEL */}
      <div id="filter-panel" hidden={!showFilters}>
        <FilterPanel
          value={state}
          facets={facets}
          categories={categories}
          onChange={update}
          onReset={reset}
        />
      </div>

      {/* ACTIVE FILTERS (visible even when the panel is closed) */}
      {chips.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mb-3">
          {chips.map((c) => (
            <button
              key={c.label}
              type="button"
              className="btn btn-sm btn-light border rounded-pill"
              onClick={c.clear}
              aria-label={`Remove filter ${c.label}`}
            >
              {c.label} <span aria-hidden="true">×</span>
            </button>
          ))}
          <button
            type="button"
            className="btn btn-sm btn-link text-danger"
            onClick={reset}
          >
            Clear all
          </button>
        </div>
      )}

      {/* RESULTS */}
      {error ? (
        <div className="text-center py-5">
          <h2 className="text-danger fs-5">{error}</h2>
          <button
            className="btn btn-outline-primary mt-3"
            onClick={() => setRetry((n) => n + 1)}
          >
            Retry
          </button>
        </div>
      ) : (
        <div style={{ opacity: loading ? 0.5 : 1 }}>
          <ProductList products={products} />

          {!loading && products.length === 0 && (
            <div className="text-center py-5">
              <p>No products match these filters.</p>
              {chips.length > 0 && (
                <button className="btn btn-outline-success" onClick={reset}>
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* PAGINATION */}
      {totalPages > 1 && (
        <nav className="d-flex justify-content-center mt-4" aria-label="Product pages">
          <ul className="pagination">
            <li className={`page-item ${state.page === 1 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => goToPage(state.page - 1)}>
                Previous
              </button>
            </li>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .slice(Math.max(0, state.page - 3), state.page + 2)
              .map((p) => (
                <li key={p} className={`page-item ${state.page === p ? "active" : ""}`}>
                  <button className="page-link" onClick={() => goToPage(p)}>
                    {p}
                  </button>
                </li>
              ))}

            <li className={`page-item ${state.page === totalPages ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => goToPage(state.page + 1)}>
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  )
}
